import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { Gift, GiftBody } from '@nhanho/shared'
import { slugifyVi } from '@nhanho/shared'
import { gifts as api, explainError } from '../../lib/api'
import { useToast } from '../../lib/toast'
import { useDirtyGuard } from '../../lib/dirty'
import { PageHeader } from '../../components/ui/PageHeader'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Field } from '../../components/ui/Field'
import { Input, inputClass } from '../../components/ui/Input'
import { Toggle } from '../../components/ui/Toggle'
import { Spinner } from '../../components/ui/Spinner'
import { ConfirmDialog } from '../../components/ui/Modal'
import { GIFT_HTML_MAX, extractTitle, formatBytes, giftUrl, usesBrowserStorage } from './giftShared'

/**
 * ⚠️ `html` **KHÔNG** nằm trong `Form`, và đó là chủ đích.
 *
 * Một file artifact là vài MB. Để nó trong state + `<textarea value={...}>` controlled là
 * mỗi phím gõ kéo theo một lần set state vài MB, một lần re-render và một lần diff —
 * tab đơ vài giây ngay khi dán. Và `dirty` theo mẫu `PostForm` chạy
 * `JSON.stringify(toBody(form))` **hai lần mỗi render**, tức serialize 10MB cho mỗi ký tự.
 *
 * Nên: textarea **uncontrolled**, nội dung sống trong `htmlRef`, state chỉ giữ những thứ
 * rẻ (độ dài, cờ đã-đụng-vào). Lúc lưu mới đọc `htmlRef.current.value`.
 */
interface Form {
  title: string
  slug: string
  slugTouched: boolean
  note: string
  published: boolean
}

const EMPTY: Form = { title: '', slug: '', slugTouched: false, note: '', published: false }

const fromDoc = (g: Gift): Form => ({
  title: g.title, slug: g.slug, slugTouched: true, note: g.note ?? '', published: g.published,
})

/** `slug` chỉ gửi khi user tự sửa — không sửa thì server tự sinh và tự tránh trùng. */
const toBody = (f: Form, html: string): GiftBody => ({
  title: f.title,
  slug: f.slugTouched && f.slug ? f.slug : undefined,
  html,
  note: f.note,
  published: f.published,
})

export default function GiftForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const [form, setForm] = useState<Form | null>(id ? null : EMPTY)
  const [initial, setInitial] = useState<Form | null>(id ? null : EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [del, setDel] = useState(false)

  const htmlRef = useRef<HTMLTextAreaElement>(null)
  /** Giá trị đã lưu — mốc để biết textarea có đổi hay chưa, và để `key` dựng lại ô. */
  const [savedHtml, setSavedHtml] = useState('')
  const [htmlTouched, setHtmlTouched] = useState(false)
  /** Chỉ số hiển thị. Tính ở `blur`/`paste`, KHÔNG ở mỗi phím gõ — xem ghi chú `Form`. */
  const [stat, setStat] = useState({ size: 0, storage: false })

  useEffect(() => {
    if (!id) return
    api.get(id)
      .then((g) => {
        const f = fromDoc(g)
        setForm(f); setInitial(f)
        setSavedHtml(g.html); setHtmlTouched(false)
        setStat({ size: g.htmlBytes, storage: usesBrowserStorage(g.html) })
      })
      .catch((e) => { toast.error(explainError(e).message); navigate('/qua-tang') })
  }, [id, navigate, toast])

  /**
   * `html` tham gia `dirty` bằng **một cờ boolean**, không bằng so sánh chuỗi: so hai
   * chuỗi 5MB mỗi render là đúng thứ cách viết này đang tránh.
   */
  const dirty = useMemo(
    () => (!!form && !!initial && JSON.stringify(form) !== JSON.stringify(initial)) || htmlTouched,
    [form, initial, htmlTouched],
  )
  useDirtyGuard(dirty)

  if (!form) return <Spinner />

  const patch = (p: Partial<Form>) => setForm((f) => (f ? { ...f, ...p } : f))
  const setTitle = (title: string) => patch({ title, slug: form.slugTouched ? form.slug : slugifyVi(title) })

  /** Đọc textarea một lần để cập nhật dung lượng + cảnh báo. Gọi ở `blur` và sau khi dán. */
  const analyze = () => {
    const v = htmlRef.current?.value ?? ''
    setStat({ size: new Blob([v]).size, storage: usesBrowserStorage(v) })
    // Dán xong mà ô Tiêu đề còn trống → mượn <title> có sẵn trong file, đỡ phải gõ lại.
    if (!form.title.trim()) {
      const t = extractTitle(v)
      if (t) setTitle(t)
    }
  }

  const doSave = async (next: Form) => {
    const html = htmlRef.current?.value ?? ''
    if (!html.trim()) { setErrors({ html: 'Chưa dán mã HTML' }); toast.error('Chưa dán mã HTML'); return }
    if (html.length > GIFT_HTML_MAX) {
      const msg = `Mã HTML quá lớn (${formatBytes(new Blob([html]).size)} / tối đa 8 MB)`
      setErrors({ html: msg }); toast.error(msg); return
    }

    setBusy(true); setErrors({})
    try {
      const body = toBody(next, html)
      const saved = id ? await api.update(id, body) : await api.create(body)
      const f = fromDoc(saved)
      setForm(f); setInitial(f)
      setSavedHtml(saved.html); setHtmlTouched(false)
      setStat({ size: saved.htmlBytes, storage: usesBrowserStorage(saved.html) })
      toast.success(saved.published ? 'Đã lưu — trang đang hiển thị' : 'Đã lưu (trang đang ẩn)')
      if (!id) navigate(`/qua-tang/${saved.id}`, { replace: true })
    } catch (e) {
      const { message, fieldErrors } = explainError(e)
      setErrors(fieldErrors); toast.error(message)
    } finally { setBusy(false) }
  }

  const remove = async () => {
    if (!id) return
    setBusy(true)
    try {
      await api.remove(id)
      toast.success('Đã xoá')
      setInitial(form); setHtmlTouched(false)
      navigate('/qua-tang')
    } catch (e) { toast.error(explainError(e).message) } finally { setBusy(false) }
  }

  const tooBig = stat.size > GIFT_HTML_MAX

  return (
    <>
      <PageHeader
        title={id ? 'Sửa trang quà tặng' : 'Trang quà tặng mới'}
        subtitle={form.slug ? `/qua-tang/${form.slug}` : undefined}
        actions={<>
          {id && <Badge tone={initial?.published ? 'success' : 'neutral'}>{initial?.published ? 'Đang hiển thị' : 'Đang ẩn'}</Badge>}
          {id && <Button variant="ghost" className="text-danger" onClick={() => setDel(true)}>Xoá</Button>}
          {form.published
            ? <Button variant="secondary" onClick={() => void doSave({ ...form, published: false })} loading={busy}>Gỡ khỏi web</Button>
            : <Button variant="secondary" onClick={() => void doSave({ ...form, published: true })} loading={busy}>Lưu & hiện</Button>}
          <Button onClick={() => void doSave(form)} loading={busy}>{dirty ? 'Lưu' : 'Đã lưu'}</Button>
        </>}
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-5">
          <Card>
            <div className="space-y-4">
              <Field label="Tiêu đề" required hint="Hiện trên tab trình duyệt. Lưu xong hệ thống ghi nó vào thẻ <title> của mã HTML." error={errors.title}>
                <Input value={form.title} onChange={(e) => setTitle(e.target.value)} className="text-base font-medium" placeholder="Chúc mừng sinh nhật Phương Anh" />
              </Field>
              <Field label="Đường dẫn" required hint="Chữ thường, số và dấu gạch ngang. Để trống khi tạo mới thì hệ thống tự sinh từ tiêu đề." error={errors.slug}>
                <Input value={form.slug} onChange={(e) => patch({ slug: e.target.value, slugTouched: true })} className="font-mono" placeholder="phuong-anh" />
              </Field>
              {form.slug && (
                <p className="text-xs text-ink-muted">
                  Link gửi cho người nhận: <span className="font-mono text-ink">{giftUrl(form.slug)}</span>
                </p>
              )}
            </div>
          </Card>

          <Card title="Mã HTML">
            <Field as="div" label="Dán nguyên file" hint="Mở artifact → Ctrl+A → Ctrl+C → dán vào đây. Dán cả file, kể cả <head> và <script> — trang được phục vụ nguyên văn, không bị layout của site đè lên." error={errors.html}>
              {/*
                `key={savedHtml.length}` dựng lại ô sau mỗi lần lưu/nạp — ô uncontrolled
                không tự nhận `defaultValue` mới. Dùng độ dài chứ không dùng cả chuỗi làm
                key: một key 5MB là thứ React phải so sánh mỗi lần render.
              */}
              <textarea
                key={`${id ?? 'new'}-${savedHtml.length}`}
                ref={htmlRef}
                defaultValue={savedHtml}
                onChange={() => { if (!htmlTouched) setHtmlTouched(true) }}
                onBlur={analyze}
                onPaste={() => window.setTimeout(analyze, 0)}
                spellCheck={false}
                placeholder="<!doctype html>…"
                className={`${inputClass} min-h-[55vh] resize-y font-mono text-xs leading-relaxed`}
              />
            </Field>

            <div className="mt-2 space-y-2 text-xs">
              <p className={tooBig ? 'font-medium text-danger' : 'text-ink-muted'}>
                Dung lượng: <strong>{formatBytes(stat.size)}</strong> / tối đa 8 MB
                {!stat.size && ' — bấm ra ngoài ô để đo'}
              </p>
              {stat.storage && (
                <p className="rounded-button bg-warning/10 px-3 py-2 text-ink">
                  ⚠️ Mã có dùng bộ nhớ trình duyệt (<code>localStorage</code>/<code>cookie</code>).
                  Trang quà tặng chạy ở <strong>chế độ cách ly</strong> để một file HTML lạ không chạm
                  được vào phần còn lại của website — nên đoạn đó sẽ báo lỗi và không chạy.
                  Bỏ nó đi, hoặc báo lại để xem có nới được không.
                </p>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <Card title="Hiển thị">
            <div className="space-y-4">
              <Toggle checked={form.published} onChange={(v) => patch({ published: v })} label={form.published ? 'Đang hiển thị trên web' : 'Ẩn khỏi web'} />
              <p className="text-xs text-ink-muted">
                Ai có link đều xem được. Trang <strong>không</strong> lên Google và không nằm trong
                danh sách công khai nào.
              </p>
              <Field label="Ghi chú" hint="Chỉ bạn thấy — tặng ai, dịp gì" error={errors.note}>
                <Input value={form.note} onChange={(e) => patch({ note: e.target.value })} placeholder="Sinh nhật chị Phương Anh 12/10" />
              </Field>
              {id && initial?.published && (
                <a href={giftUrl(form.slug)} target="_blank" rel="noopener noreferrer" className="block">
                  <Button variant="secondary" className="w-full">Mở trang</Button>
                </a>
              )}
            </div>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={del}
        onCancel={() => setDel(false)}
        onConfirm={() => void remove()}
        loading={busy}
        title="Xoá trang quà tặng"
        message={<>Xoá <strong>{form.title}</strong>? Link <span className="font-mono">/qua-tang/{form.slug}</span> sẽ không còn truy cập được.</>}
      />
    </>
  )
}
